import {
  provide, inject, ref, Ref, reactive,
} from 'vue';

import type { AnnotatorPreferences as AnnotatorPrefsIface } from './types';
import StyleManager from './StyleManager';
import type { EditAnnotationTypes } from './layers/EditAnnotationLayer';
import type { AnnotationId, StringKeyObject } from './BaseAnnotation';
import type { VisibleAnnotationTypes } from './layers';
import type { RectBounds } from './utils';
import type { TrackSupportedFeature } from './track';
import type {
  Attribute,
  AttributeFilter,
  AttributeKeyFilter,
  TimelineAttribute,
} from './use/AttributeTypes';
import type { Time } from './use/useTimeObserver';
import type {
  ImageEnhancements,
  PercentileHistogram,
  PercentileStretch,
} from './use/useImageEnhancements';
import TrackFilterControls from './TrackFilterControls';
import GroupFilterControls from './GroupFilterControls';
import CameraStore from './CameraStore';
import CameraRegistrationStore from './alignedView/CameraRegistrationStore';
import AlignedViewStore from './alignedView/AlignedViewStore';

/**
 * Type definitions are read only because injectors may mutate internal state,
 * but should never overwrite or delete the injected object.
 */

const AnnotatorPreferencesSymbol = Symbol('annotatorPreferences');
type AnnotatorPreferences = Readonly<Ref<AnnotatorPrefsIface>>;

const AttributesSymbol = Symbol('attributes');
type AttributesType = Readonly<Ref<Attribute[]>>;

const AttributesFilterSymbol = Symbol('attributesFilter');
export interface AttributesFilterType {
  attributeFilters: Readonly<Ref< {track: AttributeFilter[]; detection: AttributeFilter[]}>>;
  addAttributeFilter: (index: number, type: Attribute['belongs'], filter: AttributeFilter) => void;
  modifyAttributeFilter: (index: number, type: Attribute['belongs'], filter: AttributeFilter) => void;
  deleteAttributeFilter: (index: number, type: Attribute['belongs']) => void;
  sortAndFilterAttributes: (attributeList: Attribute[], mode: Attribute['belongs'], attribVals: StringKeyObject, sortingMode: number, filters: AttributeFilter[]) => Attribute[];
  setTimelineEnabled: (val: boolean) => void;
  setTimelineFilter: (filter: AttributeKeyFilter) => void;
  attributeTimelineData: Readonly<Ref<TimelineAttribute[]>>;
  timelineFilter: Readonly<Ref<AttributeKeyFilter>>;
  timelineEnabled: Readonly<Ref<boolean>>;
}

const DatasetIdSymbol = Symbol('datasetID');
type DatasetIdType = Readonly<Ref<string>>;

const EditingModeSymbol = Symbol('editingMode');
type EditingModeType = Readonly<Ref<false | EditAnnotationTypes>>;

const MultiSelectSymbol = Symbol('multiSelect');
type MultiSelectType = Readonly<Ref<readonly AnnotationId[]>>;

const SegmentationPointsSymbol = Symbol('segmentationPoints');
type SegmentationPointsType = Readonly<Ref<{ points: [number, number][]; labels: number[]; frameNum: number }>>;

const SegmentationCursorLoadingSymbol = Symbol('segmentationCursorLoading');
type SegmentationCursorLoadingType = Readonly<Ref<boolean>>;

const PendingSaveCountSymbol = Symbol('pendingSaveCount');
type pendingSaveCountType = Readonly<Ref<number>>;

const ProgressSymbol = Symbol('progress');
type ProgressType = Readonly<{ loaded: boolean }>;

const RevisionIdSymbol = Symbol('revisionId');
type RevisionIdType = Readonly<Ref<number>>;

const AnnotationSetSymbol = Symbol('annotationSet');
type AnnotationSetType = Readonly<Ref<string>>;

const AnnotationSetsSymbol = Symbol('annotationSets');
type AnnotationSetsType = Readonly<Ref<string[]>>;

const ComparisonSetsSymbol = Symbol('comparisonSets');
type ComparisonSetsType = Readonly<Ref<string[]>>;

const SelectedCameraSymbol = Symbol('selectedCamera');
type SelectedCameraType = Readonly<Ref<string>>;

const SelectedKeySymbol = Symbol('selectedKey');
type SelectedKeyType = Readonly<Ref<string>>;

const SelectedTrackIdSymbol = Symbol('selectedTrackId');
const EditingGroupIdSymbol = Symbol('editingGroupId');
type SelectedTrackIdType = Readonly<Ref<AnnotationId | null>>;

const EditingMultiTrackSymbol = Symbol('editingMultiTrack');
type EditingMultiTrackType = Readonly<Ref<boolean>>;

const LassoModeSymbol = Symbol('lassoMode');
export interface LassoModeContext {
  lassoModeActive: Readonly<Ref<boolean>>;
  lassoDrawing: Readonly<Ref<boolean>>;
  setLassoDrawing: (drawing: boolean) => void;
}

const TimeSymbol = Symbol('time');
type TimeType = Readonly<Time>;

const VisibleModesSymbol = Symbol('visibleModes');
type VisibleModesType = Readonly<Ref<readonly VisibleAnnotationTypes[]>>;

const ReadOnlyModeSymbol = Symbol('readOnlyMode');
type ReadOnylModeType = Readonly<Ref<boolean>>;

const ImageEnhancementsSymbol = Symbol('imageEnhancements');
type ImageEnhancementsType = Readonly<Ref<ImageEnhancements>>;

const PercentileStretchSupportedSymbol = Symbol('percentileStretchSupported');
type PercentileStretchSupportedType = Readonly<Ref<boolean>>;
const PercentileHistogramSymbol = Symbol('percentileHistogram');
type PercentileHistogramType = Readonly<Ref<PercentileHistogram | null>>;
const PercentileHistogramLoadingSymbol = Symbol('percentileHistogramLoading');
type PercentileHistogramLoadingType = Readonly<Ref<boolean>>;

/** Class-based symbols */
const CameraStoreSymbol = Symbol('cameraStore');
const CameraRegistrationSymbol = Symbol('cameraRegistration');
const AlignedViewSymbol = Symbol('alignedView');

const TrackStyleManagerSymbol = Symbol('trackTypeStyling');
const GroupStyleManagerSymbol = Symbol('groupTypeStyling');

const TrackFilterControlsSymbol = Symbol('trackFilters');
const GroupFilterControlsSymbol = Symbol('groupFilters');

/**
 * Handler interface describes all global events mutations
 * for above state
 */
export interface Handler {
  /* Save pending changes to persistence layer */
  save(set?: string): Promise<void>;
  /* Select and seek to track */
  trackSeek(AnnotationId: AnnotationId, modifiers?: { ctrl: boolean }): void;
  /* Seek Frame */
  seekFrame(frame: number): void;
  /* Toggle editing mode for track */
  trackEdit(AnnotationId: AnnotationId): void;
  /* Confirm/lock the current annotation for active recipes */
  confirmRecipe(): void;
  /* Finalize pending segmentation without deselecting (cross-camera handoff) */
  segmentationFinalizePending(): void;
  /* toggle selection mode for track */
  trackSelect(AnnotationId: AnnotationId | null, edit: boolean, modifiers?: { ctrl: boolean }): void;
  /* select tracks enclosed by a lasso polygon */
  lassoSelect(trackIds: AnnotationId[], modifiers?: { ctrl: boolean }): void;
  /* select next track in the list */
  trackSelectNext(delta: number, filteredOverride?: readonly { id: AnnotationId }[]): void;
  /* split track */
  trackSplit(AnnotationId: AnnotationId | null, frame: number): void;
  /* Add new empty track and select it */
  trackAdd(): AnnotationId;
  /* update Rectangle bounds for track */
  updateRectBounds(
    frameNum: number,
    flickNum: number,
    bounds: RectBounds,
    rotation?: number,
  ): void;
  /* Set a feature on the selected track with proper interpolation handling */
  setTrackFeature(
    frameNum: number,
    bounds: RectBounds,
    geometry: GeoJSON.Feature<TrackSupportedFeature>[],
    runAfterLogic?: boolean,
  ): void;
  /* update geojson for track */
  updateGeoJSON(
    eventType: 'in-progress' | 'editing',
    frameNum: number,
    flickNum: number,
    data: GeoJSON.Feature,
    key?: string,
    preventInterrupt?: () => void,
  ): void;
  /* Remove a whole track */
  removeTrack(AnnotationIds: AnnotationId[],
    forcePromptDisable?: boolean, cameraName?: string): void;
  /* remove a whole group */
  removeGroup(AnnotationIds: AnnotationId[]): void;
  /* Remove a single point from selected track's geometry by selected index */
  removePoint(): void;
  /* Remove an entire annotation from selected track by selected key */
  removeAnnotation(): void;
  /* selectCamera */
  selectCamera(camera: string, editMode: boolean, preserveSelection?: boolean): void;
  /* set selectFeatureHandle and selectedKey */
  selectFeatureHandle(i: number, key: string): void;
  /* set an Attribute in the metaData */
  setAttribute({ data, oldAttribute }:
    { data: Attribute; oldAttribute?: Attribute }, updateAllTracks?: boolean): void;
  /* delete an Attribute in the metaData */
  deleteAttribute({ data }: { data: Attribute }, removeFromTracks?: boolean): void;
  /* Commit the staged merge tracks */
  commitMerge(): void;
  /* Create new group */
  groupAdd(): void;
  /* Delete all selected tracks */
  deleteSelectedTracks(): void;
  /* Put UI into group editing mode */
  groupEdit(id: AnnotationId | null): void;
  /* Turn merge mode on and off */
  toggleMerge(): AnnotationId[];
  /* Remove AnnotationIds from merge */
  unstageFromMerge(ids: AnnotationId[]): void;
  /* Reload Annotation File */
  reloadAnnotations(): Promise<void>;
  setSVGFilters({
    brightness, contrast, saturation, sharpen, percentileStretch,
  }: {
    brightness?: number;
    contrast?: number;
    saturation?: number;
    sharpen?: number;
    percentileStretch?: PercentileStretch | null;
  }): void;
  /* unlink Camera Track */
  unlinkCameraTrack(trackId: AnnotationId, camera: string): void;
  /* link Camera Track */
  linkCameraTrack(baseTrackId: AnnotationId, linkTrackId: AnnotationId, camera: string): void;
  startLinking(camera: string): void;
  stopLinking(): void;
  setChange(set: string): void;
  /* Add a hole to the current polygon */
  addHole(): void;
  /* Add a new separate polygon */
  addPolygon(): void;
  /* Cancel any in-progress creation mode (hole or polygon addition) */
  cancelCreation(): void;
  /* Register callback to finalize in-progress shapes (used by LayerManager) */
  registerFinalizeCreation(cb: () => void): void;

}
const HandlerSymbol = Symbol('handler');

/**
 * Make a trivial noop handler. Useful if you only intend to
 * override some small number of values.
 * @param handle callbacl for all handler methods
 */
function dummyHandler(handle: (name: string, args: unknown[]) => void): Handler {
  return {
    save(...args) { handle('save', args); return Promise.resolve(); },
    trackSeek(...args) { handle('trackSeek', args); },
    seekFrame(...args) { handle('seekFrame', args); },
    trackEdit(...args) { handle('trackEdit', args); },
    confirmRecipe(...args) { handle('confirmRecipe', args); },
    segmentationFinalizePending(...args) { handle('segmentationFinalizePending', args); },
    trackSelect(...args) { handle('trackSelect', args); },
    lassoSelect(...args) { handle('lassoSelect', args); },
    trackSelectNext(...args) { handle('trackSelectNext', args); },
    trackSplit(...args) { handle('trackSplit', args); },
    trackAdd(...args) { handle('trackAdd', args); return 0; },
    updateRectBounds(...args) { handle('updateRectBounds', args); },
    setTrackFeature(...args) { handle('setTrackFeature', args); },
    updateGeoJSON(...args) { handle('updateGeoJSON', args); },
    removeTrack(...args) { handle('removeTrack', args); },
    removeGroup(...args) { handle('removeGroup', args); },
    removePoint(...args) { handle('removePoint', args); },
    removeAnnotation(...args) { handle('removeAnnotation', args); },
    selectCamera(...args) { handle('selectCamera', args); },
    selectFeatureHandle(...args) { handle('selectFeatureHandle', args); },
    setAttribute(...args) { handle('setAttribute', args); },
    deleteAttribute(...args) { handle('deleteAttribute', args); },
    toggleMerge(...args) { handle('toggleMerge', args); return []; },
    commitMerge(...args) { handle('commitMerge', args); },
    groupAdd(...args) { handle('groupAdd', args); },
    deleteSelectedTracks(...args) { handle('deleteSelectedTracks', args); },
    groupEdit(...args) { handle('groupEdit', args); },
    unstageFromMerge(...args) { handle('unstageFromMerge', args); },
    reloadAnnotations(...args) { handle('reloadTracks', args); return Promise.resolve(); },
    setSVGFilters(...args) { handle('setSVGFilter', args); },
    unlinkCameraTrack(...args) { handle('unlinkCameraTrack', args); },
    linkCameraTrack(...args) { handle('linkCameraTrack', args); },
    startLinking(...args) { handle('startLinking', args); },
    stopLinking(...args) { handle('stopLinking', args); },
    setChange(...args) { handle('setChange', args); },
    addHole(...args) { handle('addHole', args); },
    addPolygon(...args) { handle('addPolygon', args); },
    cancelCreation(...args) { handle('cancelCreation', args); },
    registerFinalizeCreation(...args) { handle('registerFinalizeCreation', args); },
  };
}

/**
 * State interface describes the reactive properties that should be provided
 * by a vue-media-annotator consumer.
 *
 * Implementations of most of this state is provided via src/use, but some
 * you will need to construct on your own.
 */
export interface State {
  annotatorPreferences: AnnotatorPreferences;
  attributes: AttributesType;
  cameraStore: CameraStore;
  cameraRegistration: CameraRegistrationStore;
  alignedView: AlignedViewStore;
  datasetId: DatasetIdType;
  editingMode: EditingModeType;
  groupFilters: GroupFilterControls;
  groupStyleManager: StyleManager;
  multiSelectList: MultiSelectType;
  pendingSaveCount: pendingSaveCountType;
  progress: ProgressType;
  revisionId: RevisionIdType;
  annotationSet: AnnotationSetType;
  annotationSets: AnnotationSetsType;
  comparisonSets: ComparisonSetsType;
  segmentationPoints: SegmentationPointsType;
  segmentationCursorLoading: SegmentationCursorLoadingType;
  selectedCamera: SelectedCameraType;
  selectedKey: SelectedKeyType;
  selectedTrackId: SelectedTrackIdType;
  editingGroupId: SelectedTrackIdType;
  editingMultiTrack: EditingMultiTrackType;
  time: TimeType;
  trackFilters: TrackFilterControls;
  trackStyleManager: StyleManager;
  visibleModes: VisibleModesType;
  readOnlyMode: ReadOnylModeType;
  imageEnhancements: ImageEnhancementsType;
  percentileStretchSupported: Readonly<Ref<boolean>>;
  percentileHistogram: PercentileHistogramType;
  percentileHistogramLoading: PercentileHistogramLoadingType;
}

const markChangesPending = () => { };

/**
 * make a trivial state store. Useful if you only
 * intend to override some small number of values.
 */
function dummyState(): State {
  const cameraStore = new CameraStore({ markChangesPending });
  const removeTypes = (id: AnnotationId, types: string[]) => cameraStore.removeTypes(id, types);
  const setGroupType = (
    id: AnnotationId,
    newType: string,
    confidenceVal?: number,
    currentType?: string,
  ) => {
    cameraStore.setGroupType(id, newType, confidenceVal, currentType);
  };
  const removeGroupTypes = (id: AnnotationId, types: string[]) => cameraStore.removeGroupTypes(id, types);
  const groupFilterControls = new GroupFilterControls(
    {
      sorted: cameraStore.sortedGroups,
      remove: cameraStore.removeGroups,
      markChangesPending,
      setGroupType,
      removeTypes: removeGroupTypes,
    },
  );
  const trackFilterControls = new TrackFilterControls({
    sorted: cameraStore.sortedTracks,
    remove: cameraStore.removeTracks,
    markChangesPending,
    groupFilterControls,
    lookupGroups: cameraStore.lookupGroups.bind(cameraStore),
    getTracks: (track: AnnotationId) => cameraStore.getTrackAll(track),
    renameTrackPair: (id, currentType, newType) => (
      cameraStore.renameTrackPair(id, currentType, newType)
    ),
    removeTypes,

  });
  return {
    annotatorPreferences: ref({ trackTails: { before: 20, after: 10 }, lockedCamera: { enabled: false } }),
    attributes: ref([]),
    cameraStore,
    cameraRegistration: new CameraRegistrationStore(),
    alignedView: new AlignedViewStore(),
    datasetId: ref(''),
    editingMode: ref(false),
    multiSelectList: ref([]),
    pendingSaveCount: ref(0),
    progress: reactive({ loaded: true }),
    revisionId: ref(0),
    annotationSet: ref(''),
    annotationSets: ref([]),
    comparisonSets: ref([]),
    groupFilters: groupFilterControls,
    groupStyleManager: new StyleManager({ markChangesPending }),
    segmentationPoints: ref({ points: [], labels: [], frameNum: -1 }),
    segmentationCursorLoading: ref(false),
    selectedCamera: ref('singleCam'),
    selectedKey: ref(''),
    selectedTrackId: ref(null),
    editingGroupId: ref(null),
    editingMultiTrack: ref(false),
    time: {
      frame: ref(0),
      flick: ref(0),
      frameRate: ref(0),
      originalFps: ref(null),
      isPlaying: ref(false),
    },
    trackFilters: trackFilterControls,
    trackStyleManager: new StyleManager({ markChangesPending }),
    visibleModes: ref(['rectangle', 'text'] as VisibleAnnotationTypes[]),
    readOnlyMode: ref(false),
    imageEnhancements: ref({
      brightness: 1,
      contrast: 1,
      saturation: 1,
      sharpen: 0,
    }),
    percentileStretchSupported: ref(false),
    percentileHistogram: ref(null),
    percentileHistogramLoading: ref(false),
  };
}

/**
 * Provide global state and handler for a single instance
 * of vue-media-annotator.  Multiple annotator windows
 * are currently not supported.
 *
 * @param {State} state
 * @param {Handler} handler
 * @param {}
 */
function provideAnnotator(state: State, handler: Handler, attributesFilters: AttributesFilterType) {
  provide(AnnotatorPreferencesSymbol, state.annotatorPreferences);
  provide(AttributesSymbol, state.attributes);
  provide(CameraStoreSymbol, state.cameraStore);
  provide(CameraRegistrationSymbol, state.cameraRegistration);
  provide(AlignedViewSymbol, state.alignedView);
  provide(DatasetIdSymbol, state.datasetId);
  provide(EditingModeSymbol, state.editingMode);
  provide(GroupFilterControlsSymbol, state.groupFilters);
  provide(GroupStyleManagerSymbol, state.groupStyleManager);
  provide(MultiSelectSymbol, state.multiSelectList);
  provide(PendingSaveCountSymbol, state.pendingSaveCount);
  provide(ProgressSymbol, state.progress);
  provide(RevisionIdSymbol, state.revisionId);
  provide(AnnotationSetSymbol, state.annotationSet);
  provide(AnnotationSetsSymbol, state.annotationSets);
  provide(ComparisonSetsSymbol, state.comparisonSets);
  provide(SegmentationPointsSymbol, state.segmentationPoints);
  provide(SegmentationCursorLoadingSymbol, state.segmentationCursorLoading);
  provide(TrackFilterControlsSymbol, state.trackFilters);
  provide(TrackStyleManagerSymbol, state.trackStyleManager);
  provide(SelectedCameraSymbol, state.selectedCamera);
  provide(SelectedKeySymbol, state.selectedKey);
  provide(SelectedTrackIdSymbol, state.selectedTrackId);
  provide(EditingGroupIdSymbol, state.editingGroupId);
  provide(EditingMultiTrackSymbol, state.editingMultiTrack);
  provide(TimeSymbol, state.time);
  provide(VisibleModesSymbol, state.visibleModes);
  provide(ReadOnlyModeSymbol, state.readOnlyMode);
  provide(ImageEnhancementsSymbol, state.imageEnhancements);
  provide(PercentileStretchSupportedSymbol, state.percentileStretchSupported);
  provide(PercentileHistogramSymbol, state.percentileHistogram);
  provide(PercentileHistogramLoadingSymbol, state.percentileHistogramLoading);
  provide(HandlerSymbol, handler);
  provide(AttributesFilterSymbol, attributesFilters);
}

function _handleMissing(s: symbol): Error {
  return new Error(`Missing provided object for symbol ${s.toString()}: must provideAnnotator()`);
}

function use<T>(s: symbol) {
  const v = inject<T>(s);
  if (v === undefined) {
    throw _handleMissing(s);
  }
  return v;
}

function useAnnotatorPreferences() {
  return use<AnnotatorPreferences>(AnnotatorPreferencesSymbol);
}

function useAttributes() {
  return use<AttributesType>(AttributesSymbol);
}

function useAttributesFilters() {
  return use<AttributesFilterType>(AttributesFilterSymbol);
}

function useCameraStore() {
  return use<CameraStore>(CameraStoreSymbol);
}
function useCameraRegistration() {
  return use<CameraRegistrationStore>(CameraRegistrationSymbol);
}
function useAlignedView() {
  return use<AlignedViewStore>(AlignedViewSymbol);
}
function useDatasetId() {
  return use<DatasetIdType>(DatasetIdSymbol);
}

function useEditingMode() {
  return use<EditingModeType>(EditingModeSymbol);
}

function useGroupFilterControls() {
  return use<GroupFilterControls>(GroupFilterControlsSymbol);
}

function useGroupStyleManager() {
  return use<StyleManager>(GroupStyleManagerSymbol);
}

function useHandler() {
  return use<Handler>(HandlerSymbol);
}

function useLassoModeContext() {
  return use<LassoModeContext>(LassoModeSymbol);
}

function useMultiSelectList() {
  return use<MultiSelectType>(MultiSelectSymbol);
}

function usePendingSaveCount() {
  return use<pendingSaveCountType>(PendingSaveCountSymbol);
}

function useProgress() {
  return use<ProgressType>(ProgressSymbol);
}

function useRevisionId() {
  return use<RevisionIdType>(RevisionIdSymbol);
}

function useAnnotationSet() {
  return use<AnnotationSetType>(AnnotationSetSymbol);
}

function useAnnotationSets() {
  return use<AnnotationSetsType>(AnnotationSetsSymbol);
}

function useComparisonSets() {
  return use<ComparisonSetsType>(ComparisonSetsSymbol);
}

function useTrackStyleManager() {
  return use<StyleManager>(TrackStyleManagerSymbol);
}

function useSelectedCamera() {
  return use<SelectedCameraType>(SelectedCameraSymbol);
}

function useSelectedKey() {
  return use<SelectedKeyType>(SelectedKeySymbol);
}

function useSelectedTrackId() {
  return use<SelectedTrackIdType>(SelectedTrackIdSymbol);
}

function useEditingGroupId() {
  return use<SelectedTrackIdType>(EditingGroupIdSymbol);
}

function useEditingMultiTrack() {
  return use<EditingMultiTrackType>(EditingMultiTrackSymbol);
}

function useTime() {
  return use<TimeType>(TimeSymbol);
}

function useTrackFilters() {
  return use<TrackFilterControls>(TrackFilterControlsSymbol);
}

function useVisibleModes() {
  return use<VisibleModesType>(VisibleModesSymbol);
}
function useReadOnlyMode() {
  return use<ReadOnylModeType>(ReadOnlyModeSymbol);
}
function useImageEnhancements() {
  return use<ImageEnhancementsType>(ImageEnhancementsSymbol);
}

function usePercentileStretchSupported() {
  return use<PercentileStretchSupportedType>(PercentileStretchSupportedSymbol);
}

function usePercentileHistogram() {
  return use<PercentileHistogramType>(PercentileHistogramSymbol);
}

function usePercentileHistogramLoading() {
  return use<PercentileHistogramLoadingType>(PercentileHistogramLoadingSymbol);
}

function useSegmentationPoints() {
  return use<SegmentationPointsType>(SegmentationPointsSymbol);
}

function useSegmentationCursorLoading() {
  return use<SegmentationCursorLoadingType>(SegmentationCursorLoadingSymbol);
}

export {
  LassoModeSymbol,
  dummyHandler,
  dummyState,
  provideAnnotator,
  use,
  useAnnotatorPreferences,
  useAttributes,
  useCameraStore,
  useCameraRegistration,
  useAlignedView,
  useDatasetId,
  useEditingMode,
  useHandler,
  useLassoModeContext,
  useGroupFilterControls,
  useGroupStyleManager,
  useMultiSelectList,
  usePendingSaveCount,
  useProgress,
  useRevisionId,
  useAnnotationSet,
  useAnnotationSets,
  useComparisonSets,
  useTrackFilters,
  useTrackStyleManager,
  useSelectedCamera,
  useSelectedKey,
  useSelectedTrackId,
  useEditingGroupId,
  useEditingMultiTrack,
  useTime,
  useVisibleModes,
  useReadOnlyMode,
  useImageEnhancements,
  usePercentileStretchSupported,
  usePercentileHistogram,
  usePercentileHistogramLoading,
  useAttributesFilters,
  useSegmentationPoints,
  useSegmentationCursorLoading,
};
